import VoiceToText from "@/components/VoiceToText";
import { useChatWithAI } from "@/services-react-query/queries";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  Stack,
  alpha,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IApplicationState, showMessage } from "@/store-redux";

interface Message {
  text: string;
  sender: "user" | "bot";
}

const ChatApp = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  return (
    <>
      {/* Button to open chat popup */}
      <IconButton
        sx={{
          position: "fixed",
          top: 13,
          right: 20,
          bgcolor: "#6366F1",
          color: "#fff",
          zIndex: 1200, // Above CMS layout header
          borderRadius: "12px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: isDark ? "0 0 20px rgba(99, 102, 241, 0.3)" : "0 4px 12px rgba(99, 102, 241, 0.2)",
          "&:hover": {
            bgcolor: "#4F46E5",
            transform: "translateY(-2px)",
            boxShadow: isDark ? "0 0 30px rgba(99, 102, 241, 0.5)" : "0 8px 16px rgba(99, 102, 241, 0.3)",
          },
        }}
        onClick={toggleModal}
      >
        <ChatIcon />
      </IconButton>

      {/* Chat Modal */}
      <Dialog
        open={isModalOpen}
        onClose={toggleModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            bgcolor: isDark ? "#0F172A" : "#fff",
            backgroundImage: "none",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
            boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ChatIcon sx={{ color: "#6366F1" }} />
            <Typography sx={{ fontWeight: 800, color: isDark ? "#fff" : "#0F172A" }}>
              AI Assistant <span style={{ opacity: 0.5, fontSize: "0.8em", fontWeight: 400 }}>(CMS Helper)</span>
            </Typography>
          </Stack>
          <IconButton onClick={toggleModal} size="small" sx={{ color: isDark ? "rgba(255,255,255,0.5)" : "#64748B" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: isDark ? "rgba(0,0,0,0.2)" : alpha("#F1F5F9", 0.5) }}>
          <ChatAIContent />
        </DialogContent>
      </Dialog>
    </>
  );
};

interface IChatAIContentProps {
  isPageChat?: boolean;
}

export const ChatAIContent = ({ isPageChat }: IChatAIContentProps) => {
  const dispatch = useDispatch();
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  const [messages, setMessages] = useState<Message[]>(() => {
    const savedMessages = localStorage.getItem("chatMessages");
    return savedMessages ? JSON.parse(savedMessages) : [];
  });
  const chatContainerRef = useRef<any>(null);

  useEffect(() => {
    localStorage.setItem("chatMessages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleClearChat = () => {
    setMessages([]);
    localStorage.removeItem("chatMessages");
  };

  const { mutate: chatWithAI, isLoading } = useChatWithAI({
    onSuccess(data) {
      const botMessage = { text: data?.result?.toString() ?? "", sender: "bot" };
      setMessages((prev: any) => [...prev, botMessage]);
    },
  });

  const handleSendMessage = (input: string) => {
    if (input.trim()) {
      const userMessage = { text: input, sender: "user" };
      setMessages((prev: any) => [...prev, userMessage]);
      chatWithAI({ prompt: input, type: "word_search" });
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      dispatch(showMessage("Copied to clipboard!", "success"));
    });
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: isPageChat ? "calc(100vh - 240px)" : "70vh" }}>
      {!isPageChat && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            startIcon={<DeleteIcon />}
            onClick={handleClearChat}
            size="small"
            sx={{
              color: "#F43F5E",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: alpha("#F43F5E", 0.1) },
            }}
          >
            Clear History
          </Button>
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          overflowY: "auto",
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fff",
          borderRadius: "20px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            borderRadius: "2px",
          },
        }}
        ref={chatContainerRef}
      >
        {messages.length > 0 ? (
          messages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: msg.sender === "user" ? "flex-end" : "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  maxWidth: "85%",
                  flexDirection: msg.sender === "user" ? "row-reverse" : "row",
                }}
              >
                <Typography
                  sx={{
                    bgcolor: msg.sender === "user" ? "#6366F1" : isDark ? "rgba(255,255,255,0.05)" : "#F1F5F9",
                    color: msg.sender === "user" ? "#fff" : isDark ? "#fff" : "#0F172A",
                    p: 1.5,
                    borderRadius: msg.sender === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                    boxShadow: msg.sender === "user" ? "0 4px 12px rgba(99, 102, 241, 0.2)" : "none",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.text}
                </Typography>
                {msg.sender === "bot" && (
                  <IconButton
                    size="small"
                    onClick={() => handleCopyToClipboard(msg.text)}
                    sx={{ mt: 0.5, opacity: 0.5, "&:hover": { opacity: 1 } }}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.5,
            }}
          >
            <ChatIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography align="center" variant="body2" sx={{ maxWidth: "200px" }}>
              Bạn cần tra cứu từ gì, cứ nhắn với tôi nhé!
            </Typography>
          </Box>
        )}
      </Paper>

      <Box sx={{ pt: 2 }}>
        <VoiceToText
          placeholder="Type your message..."
          onTextSubmit={(text) => handleSendMessage(text)}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default ChatApp;
